import {
  buildPLPHeadline,
  buildPLPMetaDescription,
  buildPLPTitle,
  canonicalSlugMap,
  createBreadcrumbList,
} from 'head/utils/seoHelpers'
import { getLocalePath, t } from 'localization'
import { setLocalizedHTMLAnchorLinks } from 'localization/utils/locale-utils'
import { logSpecificPageView } from 'mParticle/mParticleService'
import { NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import ErrorPage from 'pages/_error'
import { useEffect } from 'react'
import { AlgoliaIndex } from 'search'
import ProductListPageBreadcrumbs from 'search/components/ProductListPageBreadcrumbs'
import {
  combineFiltersAndNumericFilters,
  formatAlgoliaFilters,
  formatAlgoliaNumericFilters,
  getActiveFilterTypes,
} from 'search/utils/formatAlgoliaFilters'
import hideFilters from 'search/utils/hideFilters'
import { isAccessoriesPresent, isClothingPresent } from 'search/utils/isCategoryPresent'
import Divider from 'shared/components/Divider'
import Grid, { GridConfig } from 'shared/components/Grid'
import PageContextProvider from 'shared/contexts/PageContextProvider'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import { media } from 'shared/lib'
import { GetPLPServerSideProps } from 'shared/utils/getPLPServerSideProps'
import styled from 'styled-components'
import { trackSnapchatPageView } from 'tracking/snapchatService'

const TP = 'grid.FilteredGridPage'

export const GRID_PAGES = new Set([
  'a-bathing-ape',
  'adidas',
  'air-jordans',
  'and1',
  'anta',
  'apparel',
  'champion',
  'crocs',
  'curry-brand',
  'fila',
  'li-ning',
  'men',
  'new-balance',
  'new-releases',
  'nike',
  'off-white',
  'pre-owned',
  'price-drops',
  'sneakers',
  'sneakers-under-250',
  'sneakers-under-350',
  'supreme',
  'ugg',
  'women',
  'yeezy',
  'youth',
])

export const FilteredGridPage: NextPage<GetPLPServerSideProps> = ({
  baseCSSearchState,
  breadcrumbData,
  consumerSearchResult,
  consumerSearchState,
  consumerSearchTrendingResults,
  error,
  hostname = '',
  queryParamSearchState,
  resultsState,
  slug,
  ssrCSDarwinGroup, // TODO: remove once CS Experiment is finished
  trendingResultsState,
}) => {
  const router = useRouter()

  useEffect(() => {
    if (!error) {
      logSpecificPageView({
        slug,
        pageName: SpecificPageTrackingEvents.FilteredGridPage,
        slugType: 'product_list',
      })
      trackSnapchatPageView()
    }
  }, [error, slug])

  if (error) {
    return <ErrorPage statusCode={error.status} />
  }

  const { name, description, algoliaQuery, fullPath = [] } = breadcrumbData || {}
  const numericFilters = algoliaQuery?.numeric_filters || {}
  const numericFilterTypes = getActiveFilterTypes(numericFilters)
  const activeFilterTypes = getActiveFilterTypes(algoliaQuery?.filters || {})
  const productCategory = algoliaQuery?.filters?.product_category || []
  const filterString = formatAlgoliaFilters(activeFilterTypes, algoliaQuery?.filters)
  const numericFilterString = formatAlgoliaNumericFilters(numericFilterTypes, numericFilters)
  const combinedFiltersString = combineFiltersAndNumericFilters(filterString, numericFilterString)
  const hiddenFilters = hideFilters(algoliaQuery?.filters, {
    hideBrand: name === 'Yeezy',
  })
  const breadcrumbList = createBreadcrumbList(hostname, router?.locale)(fullPath)
  const config: GridConfig = {
    context: {
      hasNewProductOnly:
        isClothingPresent(productCategory) || isAccessoriesPresent(productCategory),
      hiddenFilters,
      // filter syntax per algolia:
      // https://www.algolia.com/doc/api-reference/api-parameters/filters/
      filters: combinedFiltersString,
      index: algoliaQuery?.indexName || AlgoliaIndex.Relevance,
      ruleContexts: [`collection-${slug?.replace('/', '')}`],
    },
  }

  return (
    <>
      <PageContextProvider value={SpecificPageTrackingEvents.FilteredGridPage}>
        <Head>
          <title>
            {t(`${TP}.pageTitle`, {
              defaultValue: '{title} | Flight Club',
              title: buildPLPTitle(fullPath),
            })}
          </title>
          <meta
            name="description"
            content={buildPLPMetaDescription(fullPath, resultsState?.rawResults[0]?.hits[0]?.name)}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'http://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: breadcrumbList,
              }),
            }}
          />
          {canonicalSlugMap.has(slug as string) && (
            <link
              key="canonical"
              rel="canonical"
              href={`https://www.flightclub.com${getLocalePath(
                `${canonicalSlugMap.get(slug as string)}${
                  router?.query?.page && `?page=${router?.query?.page}`
                }`,
                router?.locale,
              )}`}
            />
          )}
        </Head>

        <Grid
          baseCSSearchState={baseCSSearchState}
          config={config}
          consumerSearchResult={consumerSearchResult}
          consumerSearchState={consumerSearchState}
          consumerSearchTrendingResults={consumerSearchTrendingResults}
          description={setLocalizedHTMLAnchorLinks(description, router?.locale)}
          noResultsState={trendingResultsState}
          queryParamSearchState={queryParamSearchState}
          resultsState={resultsState}
          ssrCSDarwinGroup={ssrCSDarwinGroup}
          title={buildPLPHeadline(fullPath)}
        />
        {fullPath.length > 1 && (
          <>
            <StyledDivider data-testid="plpDivider" />
            <ProductListPageBreadcrumbs breadcrumbPath={fullPath} />
          </>
        )}
      </PageContextProvider>
    </>
  )
}

const StyledDivider = styled(Divider)`
  ${media.large`
    margin-top: 80px;
  `}
`
